import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from 'react';
import { apiRequest, ensureInstallation, setActiveSurface, type ClientSurface } from '../api/client';
import { ApiError } from '../api/problem';
import {
  clearGrants,
  grantFor,
  grantsSnapshot,
  liveGrants,
  recordGrant,
  spendGrant,
  subscribeGrants,
  type StepUpGrant,
} from './stepup';

/**
 * The session, as this application is allowed to know it.
 *
 * There is NO `GET /v1/me` on this branch (see the residual): the only authority on who the caller
 * is, is the server's answer to a real request. So the provider keeps a *marker* of the surface the
 * operator signed in on — which portal chrome to draw — and treats a `401` that names a lost
 * session as the server saying the marker is stale. The credential itself is the
 * `__Host-expo_session` cookie and is never readable here.
 *
 * ASSURANCE IS NOT PART OF THE MARKER (P13-f M10). `iam.StepUp` resets the session's assurance the
 * moment a grant is consumed, so a persisted `assurance: 'step_up'` outlives the thing it describes
 * and survives a reload the server knows nothing about. What the shell draws instead is the live
 * grant ledger: what the server actually issued, for which action, until when.
 */

export type SessionStatus = 'anonymous' | 'authenticated' | 'registration_required';

export interface SessionState {
  status: SessionStatus;
  surface: ClientSurface;
  /**
   * The assurance this TAB observed at sign-in (`mfa`, `password_otp`). It is in memory only and is
   * gone after a reload, because the client cannot read the server's current level.
   */
  observedAssurance: string | null;
  /** The registration token `POST /v1/auth/otp:verify` returns for a first-time customer. */
  registrationToken: string | null;
}

export interface StaffLoginInput {
  email: string;
  password: string;
  totpCode: string;
}

export interface PasswordLoginInput {
  phoneE164: string;
  password: string;
}

export interface OtpStartInput {
  phoneE164: string;
  purpose: string;
  locale: string;
  captchaToken?: string;
}

export interface OtpStartResult {
  otp_id: string;
  expires_at: string;
  resend_after_s: number;
  channel: string;
}

interface OtpVerifyResponse {
  status: string;
  session: { surface: string; kind: string };
  registration_token: string | null;
  expires_at: string;
}

interface StepUpResponse {
  assurance_level: string;
  expires_at: string;
}

export interface SessionValue extends SessionState {
  staffLogin: (input: StaffLoginInput) => Promise<void>;
  passwordLogin: (input: PasswordLoginInput) => Promise<void>;
  otpStart: (input: OtpStartInput) => Promise<OtpStartResult>;
  otpVerify: (otpId: string, code: string) => Promise<SessionStatus>;
  register: (input: { fullName: string; password: string; locale: string; expoId: string }) => Promise<void>;
  /** Raises assurance FOR ONE ACTION and records the grant against exactly that action. */
  stepUp: (totpCode: string, action: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Called by the query layer when the server refuses a request as an unauthenticated one. */
  markUnauthenticated: () => void;
  /** The live grant for exactly this action, or null. */
  grantFor: (action: string) => StepUpGrant | null;
  /** Every live grant — what the shell draws as the current scope. */
  grants: readonly StepUpGrant[];
  /** Drops the UI's record of a grant, e.g. when the operator abandons the action. */
  releaseGrant: (action: string) => void;
}

/**
 * The marker is a HINT about which chrome to draw, never a credential.
 *
 * It lives in `localStorage` so a reload — or a second tab — does not bounce an operator with a
 * perfectly valid `__Host-expo_session` cookie back to the sign-in form. It carries no token, no
 * identity AND NO ASSURANCE LEVEL: the surface is the only thing the client legitimately knows
 * across a reload, and the server remains the only authority on everything else.
 */
const MARKER_KEY = 'moqeem.session';

interface Marker {
  surface: ClientSurface;
}

function readMarker(): Marker | null {
  try {
    const raw = window.localStorage.getItem(MARKER_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'surface' in parsed) {
      return { surface: (parsed as Marker).surface };
    }
  } catch {
    /* storage may be unavailable; the server remains the authority */
  }
  return null;
}

function writeMarker(marker: Marker | null): void {
  try {
    if (marker) window.localStorage.setItem(MARKER_KEY, JSON.stringify({ surface: marker.surface }));
    else window.localStorage.removeItem(MARKER_KEY);
  } catch {
    /* ignore */
  }
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({
  children,
  surface,
}: {
  children: ReactNode;
  surface: ClientSurface;
}): ReactElement {
  const marker = readMarker();
  const [state, setState] = useState<SessionState>(() => ({
    status: marker && marker.surface === surface ? 'authenticated' : 'anonymous',
    surface,
    observedAssurance: null,
    registrationToken: null,
  }));

  // The grant ledger is a plain store so the query layer can spend a grant without a hook; the
  // shell subscribes to it here.
  const grants = useSyncExternalStore(subscribeGrants, grantsSnapshot, grantsSnapshot);

  useEffect(() => {
    setActiveSurface(surface);
    setState((s) => ({ ...s, surface }));
    // First contact mints `__Host-expo_install`, which every authentication endpoint's rate budget
    // is keyed by. It runs before any sign-in so the budget exists when the sign-in spends it.
    void ensureInstallation(surface);
  }, [surface]);

  const markUnauthenticated = useCallback(() => {
    writeMarker(null);
    clearGrants();
    setState((s) => ({ ...s, status: 'anonymous', observedAssurance: null }));
  }, []);

  const staffLogin = useCallback(
    async (input: StaffLoginInput) => {
      await ensureInstallation(surface);
      await apiRequest<OtpVerifyResponse>('/v1/auth/staff/login', {
        method: 'POST',
        surface,
        body: { email: input.email, password: input.password, totp_code: input.totpCode },
      });
      writeMarker({ surface });
      clearGrants();
      setState((s) => ({
        ...s,
        status: 'authenticated',
        observedAssurance: 'mfa',
        registrationToken: null,
      }));
    },
    [surface],
  );

  const passwordLogin = useCallback(
    async (input: PasswordLoginInput) => {
      await ensureInstallation(surface);
      await apiRequest<OtpVerifyResponse>('/v1/auth/login', {
        method: 'POST',
        surface,
        body: { phone_e164: input.phoneE164, password: input.password },
      });
      writeMarker({ surface });
      clearGrants();
      setState((s) => ({
        ...s,
        status: 'authenticated',
        observedAssurance: 'password_otp',
        registrationToken: null,
      }));
    },
    [surface],
  );

  const otpStart = useCallback(
    async (input: OtpStartInput) => {
      const body: Record<string, string> = {
        phone_e164: input.phoneE164,
        purpose: input.purpose,
        locale: input.locale,
        // The server decodes `captcha_token` strictly; an empty string is a valid absent value.
        captcha_token: input.captchaToken ?? '',
      };
      await ensureInstallation(surface);
      const res = await apiRequest<OtpStartResult>('/v1/auth/otp:request', {
        method: 'POST',
        surface,
        body,
      });
      return res.data;
    },
    [surface],
  );

  const otpVerify = useCallback(
    async (otpId: string, code: string) => {
      const res = await apiRequest<OtpVerifyResponse>('/v1/auth/otp:verify', {
        method: 'POST',
        surface,
        body: { otp_id: otpId, code },
      });
      const status = res.data.status;
      if (status === 'authenticated' || status === 'registered') {
        writeMarker({ surface });
        clearGrants();
        setState((s) => ({
          ...s,
          status: 'authenticated',
          observedAssurance: 'password_otp',
          registrationToken: null,
        }));
      } else {
        setState((s) => ({
          ...s,
          status: 'registration_required',
          registrationToken: res.data.registration_token,
        }));
      }
      return status === 'registered' ? 'authenticated' : status as SessionStatus;
    },
    [surface],
  );

  const register = useCallback(
    async (input: { fullName: string; password: string; locale: string; expoId: string }) => {
      // The body is `registerBody` in backend/internal/api/routes_crm.go and it is decoded
      // STRICTLY: an unknown field is a refusal, so only fields that exist are sent.
      const body: Record<string, unknown> = {
        registration_token: state.registrationToken,
        password: input.password,
        full_name: input.fullName,
        expo_id: input.expoId,
        locale: input.locale,
        consents: [
          { kind: 'terms', decision: 'granted', text_version: 'consent-2026.08.1' },
          { kind: 'privacy_notice', decision: 'granted', text_version: 'consent-2026.08.1' },
        ],
      };
      await apiRequest('/v1/auth/register', { method: 'POST', surface, body });
      writeMarker({ surface });
      setState((s) => ({
        ...s,
        status: 'authenticated',
        observedAssurance: 'password_otp',
        registrationToken: null,
      }));
    },
    [surface, state.registrationToken],
  );

  /**
   * Step-up FOR ONE ACTION.
   *
   * `POST /v1/auth/step-up` takes the action in its body and the backend evaluates
   * `RequireStepUp(az, action, now)` per route, so the grant that comes back belongs to that action
   * and to no other. It is recorded against the action string the caller will send, and nothing
   * else in this application can present it for a different mutation.
   */
  const stepUp = useCallback(
    async (totpCode: string, action: string) => {
      const res = await apiRequest<StepUpResponse>('/v1/auth/step-up', {
        method: 'POST',
        surface,
        body: { totp_code: totpCode, action },
      });
      recordGrant(action, res.data.expires_at);
    },
    [surface],
  );

  const signOut = useCallback(async () => {
    try {
      await apiRequest('/v1/auth/logout', { method: 'POST', surface });
    } catch (e) {
      // A session the server has already dropped still ends here: the marker is what draws the
      // chrome, and leaving it set after an explicit sign-out would be a lie.
      if (!(e instanceof ApiError) || !e.isSessionLost) throw e;
    }
    markUnauthenticated();
  }, [surface, markUnauthenticated]);

  const value = useMemo<SessionValue>(
    () => ({
      ...state,
      staffLogin,
      passwordLogin,
      otpStart,
      otpVerify,
      register,
      stepUp,
      signOut,
      markUnauthenticated,
      grantFor: (action: string) => grantFor(action),
      // `grants` is the store's own snapshot, re-read here so the memo is invalidated when the
      // ledger changes; `liveGrants()` additionally sweeps anything whose window has closed.
      grants: grants.length === 0 ? grants : liveGrants(),
      releaseGrant: spendGrant,
    }),
    [state, staffLogin, passwordLogin, otpStart, otpVerify, register, stepUp, signOut, markUnauthenticated, grants],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
